import i18n from "../i18n";

const CoachVideos = (app) => {
  app
    .get("/coach-videos/delete/:_id", async ({ req, res, flash, api }) => {
      const { _id } = req.params;

      await api(`coach-videos/?_id=${_id}`, {
        method: "DELETE",
      });

      flash(
        "success",
        i18n.t("coaching-portal:coachVideosActions.successfullyDeleted")
      );

      return res.backDontScroll();
    })
    /**
     * Add a comment to a coach-video thread.
     * Body: { message, senderId, threadId?, videoRecordId? }
     * If threadId omitted: creates thread, attaches to video (videoId required).
     * Redirects back with threadId in query when a new thread was created.
     */
    .post(
      "/coach-videos/add-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { message, senderId, videoRecordId } = body;
        let { threadId } = body;
        let creatingNewThread = false;

        assert(senderId, 400, "Sender ID is required");
        assert(message, 400, "Message is required");

        try {
          if (!threadId) {
            creatingNewThread = true;
            assert(videoRecordId, 400, "Video record ID is required");

            const response = await api("thread", {
              method: "POST",
            });

            const data = await response.json();

            if (!data._id) {
              throw new Error("Failed to create thread");
            }

            threadId = data._id;

            await api("coach-videos", {
              method: "PUT",
              body: { videoRecordId, threadId },
            });
          }

          await api("comment", {
            method: "POST",
            body: {
              message,
              senderId,
              receiverId: body.receiverId,
              threadId,
              activityContext: body.activityContext,
            },
          });
          flash("success", "Successfully added comment");
          res.refreshWith(
            { ...(creatingNewThread && { threadId }) },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Delete a comment from a coach-video thread.
     * Params: _id (comment id). Body: { fullDelete?, threadId?, videoRecordId? }
     * If fullDelete: detaches thread from video, deletes thread and all comments; body params required.
     * Otherwise: soft-deletes the single comment only.
     */
    .post(
      "/coach-videos/delete-thread-comment/:_id",
      async ({ req, res, flash, api, assert }) => {
        const { _id } = req.params;
        const { fullDelete, threadId, videoRecordId } = req.body;

        try {
          assert(_id, 400, "Comment ID is required");

          if (fullDelete) {
            assert(videoRecordId, 400, "Video record ID is required");
            assert(threadId, 400, "Thread ID is required");

            await api("coach-videos", {
              method: "PUT",
              body: { videoRecordId, deleteThread: true },
            });

            await api(`thread/${threadId}`, { method: "DELETE" });
            await api(`comment?threadId=${threadId}&fullDelete=true`, {
              method: "DELETE",
            });
            flash("success", "Successfully deleted thread");
            return res.refreshWith(
              { threadId: "" },
              { url: "back", hash: "#_" }
            );
          }

          await api(`comment/${_id}`, { method: "DELETE" });
          flash("success", "Successfully deleted comment");
          return res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Edit a comment in a coach-video thread.
     * Body: { commentId, message }
     * PUTs the comment, then redirects back.
     */
    .post(
      "/coach-videos/edit-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { commentId, message } = body;

        assert(commentId, 400, "Comment ID is required");
        assert(message, 400, "Message is required");

        try {
          await api(`comment/${commentId}`, {
            method: "PUT",
            body: { message, editedAt: new Date() },
          });

          flash("success", "Comment saved successfully");

          res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    );
};

export default CoachVideos;
